import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Clock } from 'lucide-react';
import { addDays, format, isSameDay, isAfter, startOfDay } from 'date-fns';

const iso = (d) => format(d, 'yyyy-MM-dd');
const fmtH = (h) => String(Math.round(h * 4) / 4).replace(/\.0+$/, '');

/** Own hours per day for the week shown on Schedule, with the weekly total. */
export const WeeklyHoursStrip = ({ weekStart, schedule, refreshKey = 0 }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const today = startOfDay(new Date());

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/portal/hours?start_date=${iso(weekStart)}&end_date=${iso(addDays(weekStart, 6))}`);
      setRows(r.data || []);
    } catch { setRows([]); }
  }, [api, weekStart]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const byDay = {};
  (rows || []).forEach(h => { byDay[h.date] = (byDay[h.date] || 0) + (h.hours || 0); });
  const total = Object.values(byDay).reduce((a, b) => a + b, 0);
  const scheduledDays = new Set((schedule || []).map(s => s.date));

  const openDay = (d) => {
    const key = iso(d);
    const entries = (rows || []).filter(h => h.date === key);
    const jobs = (schedule || []).filter(s => s.date === key);
    const pid = entries[0]?.project_id || jobs[0]?.project_id;
    if (pid) navigate(`/portal/job/${pid}?date=${key}`);
  };

  return (
    <section className="rounded-2xl bg-[#2A3240] border border-white/10 p-3" data-testid="weekly-hours">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#F5A623]" /> Hours this week</div>
        <div className="text-white font-black tabular-nums" data-testid="weekly-hours-total">{rows === null ? '–' : `${fmtH(total)}h`}</div>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map(d => {
          const key = iso(d);
          const h = byDay[key];
          const isToday = isSameDay(d, today);
          const future = isAfter(d, today);
          const missing = !future && !h && scheduledDays.has(key);
          const tappable = !!h || scheduledDays.has(key);
          return (
            <button key={key} type="button" disabled={!tappable} onClick={() => openDay(d)}
              className={`rounded-lg py-1.5 flex flex-col items-center gap-0.5 ${isToday ? 'bg-[#F5A623]/15 border border-[#F5A623]/40' : 'bg-[#1E2530]'} ${tappable ? 'active:scale-95' : 'opacity-70'}`}
              data-testid={`weekly-hours-day-${key}`}>
              <span className={`text-[10px] font-semibold ${isToday ? 'text-[#F5A623]' : 'text-gray-500'}`}>{format(d, 'EEEEE')}</span>
              <span className={`text-[11px] ${isToday ? 'text-white' : 'text-gray-400'}`}>{format(d, 'd')}</span>
              <span className={`text-sm font-bold tabular-nums ${h ? 'text-white' : missing ? 'text-amber-400' : 'text-gray-600'}`} data-testid={`weekly-hours-value-${key}`}>
                {h ? fmtH(h) : missing ? '!' : '–'}
              </span>
            </button>
          );
        })}
      </div>
      {rows !== null && days.some(d => !isAfter(d, today) && !byDay[iso(d)] && scheduledDays.has(iso(d))) && (
        <p className="text-[11px] text-amber-400/90 mt-2 px-1" data-testid="weekly-hours-missing">! = you were scheduled but no hours are recorded — tap the day to add or request them.</p>
      )}
    </section>
  );
};
